import { PASSKEY_CHALLENGE_TTL_SECONDS } from "@backchannel/tax-contracts";

/** JSON form of the request options returned by the shared WebAuthn API. */
export type PasskeyRequestOptionsJSON = {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{
    id: string;
    type: PublicKeyCredentialType;
    transports?: AuthenticatorTransport[];
  }>;
  userVerification?: UserVerificationRequirement;
  hints?: string[];
  extensions?: AuthenticationExtensionsClientInputs;
};

export type PasskeyCreationOptionsJSON = {
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  excludeCredentials?: Array<{
    id: string;
    type: PublicKeyCredentialType;
    transports?: AuthenticatorTransport[];
  }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  extensions?: AuthenticationExtensionsClientInputs;
};

export type PasskeyAttestationJSON = {
  id: string;
  rawId: string;
  type: "public-key";
  authenticatorAttachment?: AuthenticatorAttachment | null;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: AuthenticatorTransport[];
  };
};

export type PasskeyAssertionJSON = {
  id: string;
  rawId: string;
  type: "public-key";
  authenticatorAttachment?: AuthenticatorAttachment | null;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle?: string;
  };
};

export class PasskeyUnsupportedError extends Error {
  override name = "PasskeyUnsupportedError";
}

export class PasskeyCancelledError extends Error {
  override name = "PasskeyCancelledError";
}

export class PasskeyCeremonyError extends Error {
  override name = "PasskeyCeremonyError";
}

export class PasskeyTimeoutError extends PasskeyCeremonyError {
  override name = "PasskeyTimeoutError";

  constructor(message = "The passkey prompt timed out. Try again.") {
    super(message);
  }
}

export class PasskeyOriginError extends Error {
  override name = "PasskeyOriginError";
}

/**
 * A platform authenticator is allowed to keep a prompt open while it waits
 * for a device. Do not let a missing/blocked browser ceremony leave the Tax
 * shell in an infinite pending state; the server-provided timeout is honored
 * up to this cap and a cancellation remains distinguishable from a timeout.
 */
export const MAX_PASSKEY_PROMPT_TIMEOUT_MS = 60_000;

export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential === "function" &&
    typeof navigator.credentials?.get === "function" &&
    typeof navigator.credentials?.create === "function"
  );
}

/**
 * WebAuthn conditional mediation ("passkey autofill"): the platform offers
 * saved passkeys in the field's own autofill dropdown instead of a modal.
 * Feature-detected per the spec; unsupported browsers resolve `false`.
 */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  const check = window.PublicKeyCredential.isConditionalMediationAvailable;
  if (typeof check !== "function") return false;
  try {
    return await check();
  } catch {
    return false;
  }
}

/**
 * How long a conditional-mediation autofill request is left open before it
 * is restarted with a fresh challenge. Kept under the server's single-use
 * challenge TTL so a slow pick from the browser's autofill dropdown never
 * fails verification against an expired challenge.
 */
export const PASSKEY_AUTOFILL_REFRESH_MS = Math.max(
  (PASSKEY_CHALLENGE_TTL_SECONDS - 60) * 1000,
  30_000,
);

export const TAX_PASSKEY_RP_IDS = ["tax.backchannel.com.br", "localhost"] as const;

export function isTaxPasskeyHost(
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
): boolean {
  return TAX_PASSKEY_RP_IDS.some((rpId) => isWebAuthnRpIdUsable(rpId, hostname));
}

/** WebAuthn allows an RP ID only when it is the page host or its suffix. */
export function isWebAuthnRpIdUsable(
  rpId: string | undefined,
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
): boolean {
  if (!rpId) return true;
  const host = hostname.toLowerCase();
  const id = rpId.toLowerCase();
  return host === id || host.endsWith(`.${id}`);
}

function toUint8Array(bytes: ArrayBuffer | ArrayBufferView): Uint8Array {
  if (bytes instanceof ArrayBuffer) return new Uint8Array(bytes);
  return new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

export function bytesToBase64Url(bytes: ArrayBuffer | ArrayBufferView): string {
  let binary = "";
  for (const byte of toUint8Array(bytes)) binary += String.fromCharCode(byte);
  return globalThis.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function base64UrlToBytes(value: string): ArrayBuffer {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = globalThis.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

function isAbortLike(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const name = "name" in error ? String(error.name) : "";
  return name === "NotAllowedError" || name === "AbortError";
}

function isSecurityError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  return "name" in error && String(error.name) === "SecurityError";
}

function toAllowCredentials(
  descriptors: PasskeyRequestOptionsJSON["allowCredentials"],
): PublicKeyCredentialDescriptor[] | undefined {
  if (!descriptors?.length) return undefined;
  return descriptors.map((descriptor) => ({
    id: base64UrlToBytes(descriptor.id),
    type: descriptor.type,
    ...(descriptor.transports ? { transports: descriptor.transports } : {}),
  }));
}

export function toPublicKeyRequestOptions(
  options: PasskeyRequestOptionsJSON,
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
): PublicKeyCredentialRequestOptions {
  if (!options.challenge) {
    throw new PasskeyCeremonyError("The passkey challenge is missing.");
  }
  if (!isWebAuthnRpIdUsable(options.rpId, hostname)) {
    throw new PasskeyOriginError(
      "This Tax Operations hostname cannot use a Tax Operations passkey.",
    );
  }
  return {
    challenge: base64UrlToBytes(options.challenge),
    ...(options.timeout ? { timeout: options.timeout } : {}),
    ...(options.rpId ? { rpId: options.rpId } : {}),
    ...(options.userVerification ? { userVerification: options.userVerification } : {}),
    ...(options.extensions ? { extensions: options.extensions } : {}),
    allowCredentials: toAllowCredentials(options.allowCredentials),
  };
}

function isPublicKeyCredential(value: Credential | null): value is PublicKeyCredential {
  return (
    value?.type === "public-key" &&
    "rawId" in value &&
    "response" in value &&
    typeof (value as PublicKeyCredential).getClientExtensionResults === "function"
  );
}

function isAssertionResponse(
  response: AuthenticatorResponse,
): response is AuthenticatorAssertionResponse {
  return (
    "authenticatorData" in response &&
    "signature" in response &&
    response.authenticatorData instanceof ArrayBuffer
  );
}

function isAttestationResponse(
  response: AuthenticatorResponse,
): response is AuthenticatorAttestationResponse {
  return "attestationObject" in response && response.attestationObject instanceof ArrayBuffer;
}

export function toPublicKeyCreationOptions(
  options: PasskeyCreationOptionsJSON,
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
): PublicKeyCredentialCreationOptions {
  if (!options.challenge || !options.user?.id || !options.rp?.name) {
    throw new PasskeyCeremonyError("The passkey creation options are incomplete.");
  }
  if (!isWebAuthnRpIdUsable(options.rp.id, hostname)) {
    throw new PasskeyOriginError(
      "This Tax Operations hostname cannot enroll a Tax Operations passkey.",
    );
  }
  return {
    rp: options.rp,
    user: {
      id: base64UrlToBytes(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    challenge: base64UrlToBytes(options.challenge),
    pubKeyCredParams: options.pubKeyCredParams,
    ...(options.timeout ? { timeout: options.timeout } : {}),
    ...(options.attestation ? { attestation: options.attestation } : {}),
    ...(options.authenticatorSelection
      ? { authenticatorSelection: options.authenticatorSelection }
      : {}),
    ...(options.extensions ? { extensions: options.extensions } : {}),
    excludeCredentials: toAllowCredentials(options.excludeCredentials),
  };
}

export function serializeAttestation(credential: PublicKeyCredential): PasskeyAttestationJSON {
  const response = credential.response;
  if (!isAttestationResponse(response)) {
    throw new PasskeyCeremonyError("The authenticator did not return an attestation.");
  }
  const allowed = new Set<AuthenticatorTransport>(["usb", "nfc", "ble", "internal", "hybrid"]);
  const transports = (
    typeof response.getTransports === "function" ? response.getTransports() : []
  ).filter((value): value is AuthenticatorTransport =>
    allowed.has(value as AuthenticatorTransport),
  );
  return {
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: "public-key",
    authenticatorAttachment: asAuthenticatorAttachment(credential.authenticatorAttachment),
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      attestationObject: bytesToBase64Url(response.attestationObject),
      ...(transports.length ? { transports } : {}),
    },
  };
}

export async function createPasskeyAttestation(
  options: PasskeyCreationOptionsJSON,
): Promise<PasskeyAttestationJSON> {
  if (!isWebAuthnSupported()) {
    throw new PasskeyUnsupportedError("This browser does not support passkeys.");
  }

  let credential: Credential | null;
  const timeoutMs = Math.min(
    typeof options.timeout === "number" && Number.isFinite(options.timeout) && options.timeout > 0
      ? options.timeout
      : MAX_PASSKEY_PROMPT_TIMEOUT_MS,
    MAX_PASSKEY_PROMPT_TIMEOUT_MS,
  );
  const controller = new AbortController();
  let timedOut = false;
  let rejectTimeout!: (reason: Error) => void;
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    rejectTimeout = reject;
  });
  const timeoutId = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
    rejectTimeout(new PasskeyTimeoutError());
  }, timeoutMs);

  try {
    credential = await Promise.race([
      navigator.credentials.create({
        publicKey: toPublicKeyCreationOptions(options),
        signal: controller.signal,
      }),
      timeoutPromise,
    ]);
  } catch (error) {
    if (timedOut) throw new PasskeyTimeoutError();
    if (isAbortLike(error)) throw new PasskeyCancelledError();
    if (isSecurityError(error)) throw new PasskeyOriginError();
    throw error;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }

  if (!isPublicKeyCredential(credential)) {
    throw new PasskeyCancelledError();
  }
  return serializeAttestation(credential);
}

function asAuthenticatorAttachment(value: string | null): AuthenticatorAttachment | null {
  return value === "platform" || value === "cross-platform" ? value : null;
}

export function serializeAssertion(credential: PublicKeyCredential): PasskeyAssertionJSON {
  const response = credential.response;
  if (!isAssertionResponse(response)) {
    throw new PasskeyCeremonyError("The authenticator did not return an assertion.");
  }
  return {
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: "public-key",
    authenticatorAttachment: asAuthenticatorAttachment(credential.authenticatorAttachment),
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      authenticatorData: bytesToBase64Url(response.authenticatorData),
      signature: bytesToBase64Url(response.signature),
      ...(response.userHandle ? { userHandle: bytesToBase64Url(response.userHandle) } : {}),
    },
  };
}

export type PasskeyAssertionCeremony = {
  /**
   * Caller-owned abort signal. Passing one hands lifecycle control to the
   * caller: no internal timeout is applied, since a conditional-mediation
   * request is meant to sit open for as long as its autofill field is on
   * screen. The caller aborts it explicitly instead (another method chosen,
   * the field unmounts, or a periodic challenge refresh).
   */
  signal?: AbortSignal;
  /** `"conditional"` requests passkey autofill instead of a modal prompt. */
  mediation?: CredentialMediationRequirement;
};

export async function getPasskeyAssertion(
  options: PasskeyRequestOptionsJSON,
  ceremony: PasskeyAssertionCeremony = {},
): Promise<PasskeyAssertionJSON> {
  if (!isWebAuthnSupported()) {
    throw new PasskeyUnsupportedError("This browser does not support passkeys.");
  }

  let credential: Credential | null;

  if (ceremony.signal) {
    try {
      credential = await navigator.credentials.get({
        publicKey: toPublicKeyRequestOptions(options),
        signal: ceremony.signal,
        ...(ceremony.mediation ? { mediation: ceremony.mediation } : {}),
      });
    } catch (error) {
      if (isAbortLike(error)) throw new PasskeyCancelledError();
      if (isSecurityError(error)) throw new PasskeyOriginError();
      throw error;
    }
  } else {
    const timeoutMs = Math.min(
      typeof options.timeout === "number" && Number.isFinite(options.timeout) && options.timeout > 0
        ? options.timeout
        : MAX_PASSKEY_PROMPT_TIMEOUT_MS,
      MAX_PASSKEY_PROMPT_TIMEOUT_MS,
    );
    const controller = new AbortController();
    let timedOut = false;
    let rejectTimeout!: (reason: Error) => void;
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
      rejectTimeout = reject;
    });
    const timeoutId = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
      rejectTimeout(new PasskeyTimeoutError());
    }, timeoutMs);

    try {
      credential = await Promise.race([
        navigator.credentials.get({
          publicKey: toPublicKeyRequestOptions(options),
          signal: controller.signal,
        }),
        timeoutPromise,
      ]);
    } catch (error) {
      if (timedOut) throw new PasskeyTimeoutError();
      if (isAbortLike(error)) throw new PasskeyCancelledError();
      if (isSecurityError(error)) throw new PasskeyOriginError();
      throw error;
    } finally {
      globalThis.clearTimeout(timeoutId);
    }
  }

  if (!isPublicKeyCredential(credential)) {
    throw new PasskeyCancelledError();
  }
  return serializeAssertion(credential);
}
